import {
  can, COURSE_FIELD_LABEL, COURSE_FIELDS, TEACHER_DAY_LABEL, TEACHER_DAYS, TEACHER_HOURS, TEACHER_LIMITS, TEACHER_MODE_LABEL, TEACHER_MODES, TEACHER_REQUEST_LABEL,
  TEACHER_STATUS_LABEL, wishKey, wishTimesText,
  type CourseField, type TeacherMode, type TeacherProfile, type TeacherRequestStatus,
} from '@selfon/domain/browser';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type MyTeacherRequest, type TeacherDeskRequest, type TeacherRow } from '../api';
import { useAuth } from '../auth/AuthContext';
import { principalOf } from '../components/Layout';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { dateOnly, won } from '../format';
import './programs.css';

/*
 * 선생님 찾기: 보호자는 센터에 다니지 않아도 공개된 선생님을 찾아 수업을 신청한다.
 * 선생님은 소개를 쓰고 받은 신청에 답하며, 원장은 소속 선생님의 공개를 승인한다.
 * 수락 전에는 선생님 화면에 아이의 만 나이와 전할 말만 보인다. 결제는 다루지 않는다.
 */

type FieldFilter = CourseField | 'ALL';
const tone = (s: TeacherRequestStatus) => (s === 'ACCEPTED' ? 'ok' : s === 'PENDING' ? 'warn' : 'muted');
const feeText = (fee?: number) => (fee === undefined ? '상담 후 안내' : `회당 ${won(fee)}`);
const fieldsText = (list: CourseField[]) => list.map((f) => COURSE_FIELD_LABEL[f]).join(' · ');
const modesText = (list: TeacherMode[]) => list.map((m) => TEACHER_MODE_LABEL[m]).join(' · ');
const active = (r: MyTeacherRequest) => r.status === 'PENDING' || r.status === 'ACCEPTED';

/** 보호자 화면 */
export function GuardianTeachers() {
  const q = useLoad(() => api.teachers(), []);
  const [query, setQuery] = useState('');
  const [field, setField] = useState<FieldFilter>('ALL');
  const [mode, setMode] = useState<TeacherMode | 'ALL'>('ALL');
  const [applying, setApplying] = useState<TeacherRow | null>(null);
  const [childId, setChildId] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [wishTimes, setWishTimes] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  const [cancelling, setCancelling] = useState<MyTeacherRequest | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const children = q.data?.children ?? [];
  const requests = q.data?.requests ?? [];
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const view = (q.data?.teachers ?? []).filter((t) => (field === 'ALL' || t.fields.includes(field)) && (mode === 'ALL' || t.modes.includes(mode))
    && words.every((w) => `${t.name} ${t.title} ${t.region} ${t.intro}`.toLowerCase().includes(w)));
  const childName = (id: string) => children.find((c) => c.id === id)?.name ?? '자녀';
  // 이 화면을 열면 선생님의 답변을 본 것으로 표시한다
  const unread = requests.some((r) => r.guardianUnread);
  useEffect(() => { if (unread) void api.seenTeacherRequests().catch(() => undefined); }, [unread]);
  const free = (t: TeacherRow) => children.filter((c) => !requests.some((r) => r.teacherId === t.userId && r.childId === c.id && active(r)));
  const start = (t: TeacherRow) => {
    setChildId(free(t)[0]?.id ?? ''); setPhone(q.data?.phone ?? ''); setNote(''); setWishTimes([]); setConsent(false); setNotice(null); setApplying(t);
  };

  return (
    <div className="courses">
      <PageHeader eyebrow="혼자 고민하지 않아도 괜찮아요" title="선생님 찾기" sub="센터에 다니지 않아도 선생님을 찾아 수업을 신청할 수 있어요." />
      {q.error && <Alert>{q.error}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}
      {q.data && !children.length && <Alert kind="info">수업을 신청하려면 먼저 <Link to="/family">우리 아이</Link>에서 자녀를 등록해 주세요.</Alert>}

      {requests.length > 0 && (
        <section className="card" aria-labelledby="my-teacher-requests">
          <h2 id="my-teacher-requests">내 신청</h2>
          <ul className="teacher-requests">
            {requests.map((r) => (
              <li key={r.id}>
                <div>
                  <strong>{r.teacherName} 선생님</strong> <span className="muted small">{childName(r.childId)} · {dateOnly(r.createdAt)}</span>
                  <p className="muted small">희망 시간: {wishTimesText(r.wishTimes)}</p>
                  {r.status === 'ACCEPTED' && <p className="small">선생님이 수락했습니다. 곧 남겨 주신 번호로 연락드립니다.</p>}
                  {r.reason && <p className="muted small">{r.reason}</p>}
                </div>
                <span className="teacher-request-side">
                  <Chip tone={tone(r.status)}>{TEACHER_REQUEST_LABEL[r.status]}</Chip>
                  {active(r) && <button type="button" className="btn btn-outline" onClick={() => setCancelling(r)}>취소</button>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="course-filters">
        <div className="course-search"><input type="search" aria-label="선생님 검색" placeholder="이름, 지역으로 찾기" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
        <div className="course-chips" role="group" aria-label="분야">
          {(['ALL', ...COURSE_FIELDS] as const).map((f) => <button key={f} type="button" className="course-chip" aria-pressed={field === f} onClick={() => setField(f)}>{f === 'ALL' ? '전체' : COURSE_FIELD_LABEL[f]}</button>)}
        </div>
        <div className="course-chips" role="group" aria-label="수업 방식">
          {(['ALL', ...TEACHER_MODES] as const).map((m) => <button key={m} type="button" className="course-chip" aria-pressed={mode === m} onClick={() => setMode(m)}>{m === 'ALL' ? '모든 방식' : TEACHER_MODE_LABEL[m]}</button>)}
        </div>
      </div>

      {q.loading && !q.data ? <Loading /> : !view.length ? <Empty>{q.data?.teachers.length ? '조건에 맞는 선생님이 없습니다.' : '아직 공개된 선생님이 없습니다.'}</Empty> : (
        <ul className="course-grid">
          {view.map((t) => (
            <li key={t.userId} className="course-card">
              <span className="course-tag">{fieldsText(t.fields)}</span>
              <h3>{t.name} 선생님</h3>
              <p className="course-summary">{t.intro}</p>
              <dl className="course-meta">
                <div><dt>직함</dt><dd>{t.title}</dd></div>
                <div><dt>소속</dt><dd>{t.centerName}</dd></div>
                <div><dt>지역</dt><dd>{t.region}</dd></div>
                <div><dt>방식</dt><dd>{modesText(t.modes)}</dd></div>
                <div><dt>비용</dt><dd>{feeText(t.fee)}</dd></div>
              </dl>
              <button type="button" className="btn btn-primary" disabled={!free(t).length} onClick={() => start(t)}>{children.length && !free(t).length ? '신청함' : '수업 신청'}</button>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small course-foot">소개는 선생님이 직접 썼고, 소속 센터 원장님이나 SelfOn 운영팀이 공개를 승인했습니다. 자격의 진위까지 보증하지는 않으니 첫 상담 때 자격증을 확인해 주세요. 수업 방법과 비용은 선생님과 직접 정합니다.</p>

      <Dialog title={`${applying?.name ?? ''} 선생님께 수업 신청`} open={Boolean(applying)} onClose={() => setApplying(null)} submitLabel="신청하기"
        onSubmit={async () => {
          await api.requestTeacher(childId, { teacherId: applying!.userId, phone, contactConsent: consent, wishTimes, note: note.trim() || undefined });
          setNotice(`${applying!.name} 선생님께 신청을 보냈습니다. 수락하면 연락을 드립니다.`);
          q.reload();
        }}>
        {applying && free(applying).length > 1 && (
          <Field id="tr-child" label="신청할 아이">
            <select id="tr-child" value={childId} onChange={(e) => setChildId(e.target.value)}>{free(applying).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          </Field>
        )}
        <Field id="tr-phone" label="연락받을 휴대폰 번호"><input id="tr-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <WishTimePicker value={wishTimes} onChange={setWishTimes} />
        <Field id="tr-note" label={`선생님께 전할 말 (선택, ${TEACHER_LIMITS.note}자까지)`} hint="건강·장애 정보 같은 민감한 내용은 첫 상담 때 직접 말씀해 주세요.">
          <textarea id="tr-note" rows={3} maxLength={TEACHER_LIMITS.note} value={note} onChange={(e) => setNote(e.target.value)} aria-describedby="tr-note-hint" />
        </Field>
        <p className="muted small">신청하면 선생님에게 아이의 만 나이, 희망 시간, 전할 말만 보입니다.</p>
        <label className="teacher-consent"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> 선생님이 수락하면 제 이름, 연락처, 아이 이름을 선생님께 전달하는 데 동의합니다.</label>
      </Dialog>

      <Dialog danger title="수업 신청 취소" open={Boolean(cancelling)} onClose={() => setCancelling(null)} submitLabel="신청 취소"
        onSubmit={async () => { await api.cancelTeacherRequest(cancelling!.id); setNotice('신청을 취소했습니다. 선생님에게 전달된 연락처도 지웠습니다.'); q.reload(); }}>
        <p>{cancelling?.teacherName} 선생님께 보낸 신청을 취소합니다. 연락처는 선생님 화면에서 지워집니다.</p>
      </Dialog>
    </div>
  );
}

/** 요일 × 시간(1시간 단위) 표. 칸마다 체크박스라 키보드와 화면 낭독기로도 고를 수 있다. */
function WishTimePicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (key: string) => onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key]);
  return (
    <fieldset className="teacher-picks teacher-times">
      <legend>수업을 희망하는 요일과 시간</legend>
      <div className="table-wrap"><table>
        <thead><tr><td />{TEACHER_DAYS.map((d) => <th key={d} scope="col">{TEACHER_DAY_LABEL[d]}</th>)}</tr></thead>
        <tbody>
          {TEACHER_HOURS.map((h) => (
            <tr key={h}>
              <th scope="row">{h}시</th>
              {TEACHER_DAYS.map((d) => (
                <td key={d}><input type="checkbox" aria-label={`${TEACHER_DAY_LABEL[d]}요일 ${h}시`} checked={value.includes(wishKey(d, h))} onChange={() => toggle(wishKey(d, h))} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table></div>
      {value.length > 0 && <p className="muted small">{wishTimesText(value)}</p>}
    </fieldset>
  );
}

/** 선생님·원장 화면: 내 소개, 받은 신청, (원장) 공개 승인 */
export function TeacherDesk() {
  const { me } = useAuth();
  const q = useLoad(() => api.teacherDesk(), []);
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState<{ r: TeacherDeskRequest; accept: boolean } | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const profile = q.data?.profile ?? null;
  const act = (fn: () => Promise<unknown>) => { setError(null); fn().then(q.reload, (e) => setError(errorText(e))); };
  return (
    <div className="courses">
      <PageHeader title="선생님 소개" sub={me?.centerId ? '공개하면 센터에 다니지 않는 보호자도 선생님을 찾아 수업을 신청할 수 있습니다.' : '개인 선생님의 소개는 SelfOn 운영팀이 자격 정보를 확인한 뒤 공개합니다.'} />
      {q.error && <Alert>{q.error}</Alert>}
      {error && <Alert>{error}</Alert>}
      {q.loading && !q.data ? <Loading /> : (
        <>
          <section className="card" aria-labelledby="my-teacher-profile">
            <div className="teacher-head">
              <h2 id="my-teacher-profile">내 소개</h2>
              <Chip tone={profile?.status === 'LISTED' ? 'ok' : profile?.status === 'PENDING' ? 'warn' : 'muted'}>{TEACHER_STATUS_LABEL[profile?.status ?? 'DRAFT']}</Chip>
            </div>
            {profile ? (
              <dl className="course-detail">
                <div><dt>직함</dt><dd>{profile.title}</dd></div>
                <div><dt>분야</dt><dd>{fieldsText(profile.fields)}</dd></div>
                <div><dt>수업 방식</dt><dd>{modesText(profile.modes)}</dd></div>
                <div><dt>지역</dt><dd>{profile.region}</dd></div>
                <div><dt>수업료</dt><dd>{feeText(profile.fee)}</dd></div>
                <div><dt>소개</dt><dd>{profile.intro}</dd></div>
              </dl>
            ) : <p className="muted">아직 소개를 쓰지 않았습니다.</p>}
            <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>{profile ? '소개 수정' : '소개 쓰기'}</button>
          </section>

          <section className="card" aria-labelledby="teacher-inbox">
            <h2 id="teacher-inbox">받은 수업 신청</h2>
            {!q.data?.requests.length ? <p className="muted">아직 받은 신청이 없습니다.</p> : (
              <ul className="teacher-requests">
                {q.data.requests.map((r) => (
                  <li key={r.id}>
                    <div>
                      <strong>{r.contact ? r.contact.childName : `만 ${r.childAge}세 아이`}</strong> <span className="muted small">{dateOnly(r.createdAt)}</span>
                      <p className="small">희망 시간: {wishTimesText(r.wishTimes)}</p>
                      {r.note && <p className="small">{r.note}</p>}
                      {r.contact && <p className="small">보호자 {r.contact.guardianName} · <a href={`tel:${r.contact.phone}`}>{r.contact.phone}</a></p>}
                      {r.status === 'PENDING' && <p className="muted small">수락하면 보호자 연락처와 아이 이름이 보입니다.</p>}
                    </div>
                    <span className="teacher-request-side">
                      <Chip tone={tone(r.status)}>{TEACHER_REQUEST_LABEL[r.status]}</Chip>
                      {r.status === 'PENDING' && <>
                        <button type="button" className="btn btn-primary" onClick={() => { setReason(''); setDeciding({ r, accept: true }); }}>수락</button>
                        <button type="button" className="btn btn-outline" onClick={() => { setReason(''); setDeciding({ r, accept: false }); }}>거절</button>
                      </>}
                      {r.status === 'ACCEPTED' && <button type="button" className="btn btn-outline" onClick={() => act(() => api.endTeacherRequest(r.id, r.version))}>수업 종료</button>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {me?.centerId && can(principalOf(me), 'teacher:approve') && <ReviewList centerId={me.centerId} />}
        </>
      )}
      {editing && <ProfileDialog profile={profile} independent={!me?.centerId} onClose={() => setEditing(false)} onSaved={q.reload} />}
      <Dialog title={deciding?.accept ? '수업 신청 수락' : '수업 신청 거절'} open={Boolean(deciding)} onClose={() => setDeciding(null)} submitLabel={deciding?.accept ? '수락' : '거절'} danger={deciding ? !deciding.accept : false}
        onSubmit={async () => { await api.decideTeacherRequest(deciding!.r.id, { accept: deciding!.accept, expectedVersion: deciding!.r.version, reason: reason.trim() || undefined }); q.reload(); }}>
        {deciding?.accept ? <p>수락하면 보호자 연락처와 아이 이름이 보입니다. 보호자에게 먼저 연락해 주세요.</p> : (
          <Field id="td-reason" label={`거절 사유 (선택, ${TEACHER_LIMITS.reason}자까지)`} hint="보호자에게 보입니다.">
            <textarea id="td-reason" rows={2} maxLength={TEACHER_LIMITS.reason} value={reason} onChange={(e) => setReason(e.target.value)} aria-describedby="td-reason-hint" />
          </Field>
        )}
      </Dialog>
    </div>
  );
}

function ProfileDialog({ profile, independent, onClose, onSaved }: { profile: TeacherProfile | null; independent: boolean; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(profile?.title ?? '');
  const [fields, setFields] = useState<CourseField[]>(profile?.fields ?? []);
  const [modes, setModes] = useState<TeacherMode[]>(profile?.modes ?? []);
  const [region, setRegion] = useState(profile?.region ?? '');
  const [intro, setIntro] = useState(profile?.intro ?? '');
  const [fee, setFee] = useState(profile?.fee === undefined ? '' : String(profile.fee));
  const [credential, setCredential] = useState(profile?.credential ?? '');
  const [publish, setPublish] = useState(profile ? profile.status !== 'DRAFT' : true);
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <Dialog title="내 소개" open onClose={onClose} submitLabel="저장" wide
      onSubmit={async () => {
        await api.saveTeacherProfile({ title, fields, modes, region, intro, fee: fee.trim() ? Number(fee) : undefined, credential: credential.trim() || undefined, publish, expectedVersion: profile?.version ?? 0 });
        onSaved();
      }}>
      <Field id="tp-title" label="직함"><input id="tp-title" maxLength={TEACHER_LIMITS.title} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 언어재활사" /></Field>
      <fieldset className="teacher-picks"><legend>분야</legend>
        {COURSE_FIELDS.map((f) => <label key={f}><input type="checkbox" checked={fields.includes(f)} onChange={() => setFields(toggle(fields, f))} /> {COURSE_FIELD_LABEL[f]}</label>)}
      </fieldset>
      <fieldset className="teacher-picks"><legend>수업 방식</legend>
        {TEACHER_MODES.map((m) => <label key={m}><input type="checkbox" checked={modes.includes(m)} onChange={() => setModes(toggle(modes, m))} /> {TEACHER_MODE_LABEL[m]}</label>)}
      </fieldset>
      <Field id="tp-region" label="수업 지역"><input id="tp-region" maxLength={TEACHER_LIMITS.region} value={region} onChange={(e) => setRegion(e.target.value)} placeholder="예: 서울 마포구" /></Field>
      <Field id="tp-fee" label="회당 수업료 (선택, 원)"><input id="tp-fee" inputMode="numeric" value={fee} onChange={(e) => setFee(e.target.value)} /></Field>
      {independent && (
        <Field id="tp-cred" label="자격 정보" hint="자격증 이름과 발급 기관, 번호를 적어 주세요. SelfOn 운영팀만 보고, 보호자에게는 보이지 않습니다.">
          <input id="tp-cred" required maxLength={TEACHER_LIMITS.credential} value={credential} onChange={(e) => setCredential(e.target.value)} aria-describedby="tp-cred-hint" />
        </Field>
      )}
      <Field id="tp-intro" label={`소개 (${TEACHER_LIMITS.intro}자까지)`} hint="보호자에게 그대로 보입니다. 다른 아이의 사례나 개인정보는 쓰지 마세요.">
        <textarea id="tp-intro" rows={4} maxLength={TEACHER_LIMITS.intro} value={intro} onChange={(e) => setIntro(e.target.value)} aria-describedby="tp-intro-hint" />
      </Field>
      <label className="teacher-consent"><input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} /> 선생님 찾기에 공개합니다. 내용을 고치면 승인을 다시 받습니다.</label>
    </Dialog>
  );
}

function ReviewList({ centerId }: { centerId: string }) {
  const q = useLoad(() => api.teacherProfiles(centerId), [centerId]);
  const [error, setError] = useState<string | null>(null);
  const review = (t: TeacherProfile, approve: boolean) => { setError(null); api.reviewTeacherProfile(centerId, t.userId, { approve, expectedVersion: t.version }).then(q.reload, (e) => setError(errorText(e))); };
  const list = (q.data?.profiles ?? []).filter((t) => t.status !== 'DRAFT');
  return (
    <section className="card" aria-labelledby="teacher-review">
      <h2 id="teacher-review">소속 선생님 공개 승인</h2>
      {(q.error || error) && <Alert>{q.error ?? error}</Alert>}
      {!list.length ? <p className="muted">승인할 소개가 없습니다.</p> : (
        <ul className="teacher-requests">
          {list.map((t) => (
            <li key={t.userId}>
              <div>
                <strong>{t.name}</strong> <span className="muted small">{t.title} · {t.region}</span>
                <p className="small">{t.intro}</p>
              </div>
              <span className="teacher-request-side">
                <Chip tone={t.status === 'LISTED' ? 'ok' : 'warn'}>{TEACHER_STATUS_LABEL[t.status]}</Chip>
                {t.status === 'PENDING' && <button type="button" className="btn btn-primary" onClick={() => review(t, true)}>승인</button>}
                <button type="button" className="btn btn-outline" onClick={() => review(t, false)}>{t.status === 'LISTED' ? '공개 내리기' : '돌려보내기'}</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** 본사: 센터가 없는 개인 선생님의 공개 승인 */
export function HqTeachers() {
  const q = useLoad(() => api.hqTeacherProfiles(), []);
  const [error, setError] = useState<string | null>(null);
  const review = (t: TeacherProfile, approve: boolean) => { setError(null); api.hqReviewTeacherProfile(t.userId, { approve, expectedVersion: t.version }).then(q.reload, (e) => setError(errorText(e))); };
  const list = (q.data?.profiles ?? []).filter((t) => t.status !== 'DRAFT');
  return (
    <div className="courses">
      <PageHeader title="개인 선생님 승인" sub="자격증 원본과 본인 여부를 확인한 뒤 승인하세요. 아이를 직접 만나는 분들입니다." />
      {(q.error || error) && <Alert>{q.error ?? error}</Alert>}
      {q.loading && !q.data ? <Loading /> : !list.length ? <Empty>승인할 소개가 없습니다.</Empty> : (
        <ul className="teacher-requests card">
          {list.map((t) => (
            <li key={t.userId}>
              <div>
                <strong>{t.name}</strong> <span className="muted small">{t.title} · {t.region}</span>
                <p className="small">{t.intro}</p>
                <p className="small">자격 정보: {t.credential}</p>
              </div>
              <span className="teacher-request-side">
                <Chip tone={t.status === 'LISTED' ? 'ok' : 'warn'}>{TEACHER_STATUS_LABEL[t.status]}</Chip>
                {t.status === 'PENDING' && <button type="button" className="btn btn-primary" onClick={() => review(t, true)}>승인</button>}
                <button type="button" className="btn btn-outline" onClick={() => review(t, false)}>{t.status === 'LISTED' ? '공개 내리기' : '돌려보내기'}</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
